import React, {FC, useEffect, useRef, useState} from "react";
import {Modal, Toast} from "antd-mobile";
import {useDispatch} from "react-redux";
import {useNavigate} from "react-router-dom";
import {useMount} from "ahooks";
import {AuthState} from "@store/auth.slice";
import * as payStore from "@store/pay.slice";
import {gatherList} from "@api/order";
import {usePageConfig} from "@utils/app_hooks";
import Copyright from "@components/Copyright/index";
import "./index.scss";

// 容错读取 gather_list 里中台那包数据（照小程序 oilenginelinkage:80-87）：
// 中台返回是扁平 JSON，但我们自己的 gather_list 可能原样透传（字段在顶层），
// 也可能套了 {code:200,data:{...}} 外壳（字段在 data 层）。两层都试，哪层带中台标志就用哪层。
export function pickGatherBody(res: any): any {
  if (!res) return {};
  const d = res.data;
  if (d && (d.ok !== undefined || d.code !== undefined || d.candidates !== undefined)) {
    return d;
  }
  return res;
}

// NO_ORDER_YET 的轮询总时长（照小程序 :90），到点还没单 → 弹"重试/手动输入"
const NO_ORDER_TIMEOUT_MS = 15000;

interface CascadeItem {
  name: string; // 油号，如 "92#"
  children: any[]; // 该油号下的油枪列表（保留后端原始 list 项：id/name/goods_id/goods）
}

interface DisplayGun {
  id: number | string;
  name: string;
}

export interface OilEngineLinkageProps {
  userInfo: AuthState["user_pay_info"];
  onSwitchManual: () => void; // 切手动：通知父级渲染 StoreOil
}

// 拆分数组：把 cate[].list[] 按 goods.name 拆成 油号→油枪 二级（照小程序 :294-318）
function toCascadeList(cate: any[]): CascadeItem[] {
  const map = new Map<string, any[]>();
  (cate || []).forEach((group: any) => {
    (group.list || []).forEach((gun: any) => {
      const oilName = gun.goods.name;
      if (!map.has(oilName)) map.set(oilName, []);
      // 保留后端返回的完整 list 项（含 goods_id/goods.type 等下单要用的字段）
      map.get(oilName)!.push(gun);
    });
  });
  return Array.from(map, ([name, children]) => ({name, children}));
}

const OilEngineLinkage: FC<OilEngineLinkageProps> = (props) => {
  const {userInfo, onSwitchManual} = props;
  const navigate = useNavigate();
  const disPatch: (...args: unknown[]) => Promise<any> = useDispatch();
  const {setOilType, setGatherOrder} = payStore.paySlice.actions;
  const is_show_receipt = usePageConfig("receipt_button");

  const [oilList, setOilList] = useState<CascadeItem[]>([]);
  const [currentOilIdx, setCurrentOilIdx] = useState(0);
  const [currentGunId, setCurrentGunId] = useState<number | string | null>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState(0);

  // gather_list 轮询计时器 / 本轮查询起始时间
  const gatherTimer = useRef<any>(null);
  const gatherStartAt = useRef(0);

  useMount(() => {
    const cascade = toCascadeList(userInfo.cate);
    setOilList(cascade);
    if (cascade.length > 0) setCurrentOilIdx(0);
  });

  // 卸载时清掉轮询
  useEffect(() => {
    return () => {
      if (gatherTimer.current) clearTimeout(gatherTimer.current);
    };
  }, []);

  // 当前油号下的油枪列表（末尾追加"其它"）
  const getDisplayGuns = (): DisplayGun[] => {
    if (!oilList.length || currentOilIdx < 0) return [{id: "other", name: "其它"}];
    const target = oilList[currentOilIdx];
    const guns: DisplayGun[] = (target.children || []).map((g: any) => ({id: g.id, name: g.name}));
    guns.push({id: "other", name: "其它"});
    return guns;
  };

  // 切手动（照小程序 goManual）：清轮询 → 通知父级渲染 StoreOil
  const goManual = () => {
    if (gatherTimer.current) clearTimeout(gatherTimer.current);
    // 切手动：清掉查单候选，避免污染后续手动下单（userCreateOrder 会透传 gather_order）
    disPatch(setGatherOrder(null));
    onSwitchManual();
  };

  // 查不到单且属"输错枪号"类（NO_ORDER_YET/FUEL_MISMATCH）：只给"重选枪号"，不给手动
  const promptRetry = (content: string) => {
    if (gatherTimer.current) clearTimeout(gatherTimer.current);
    Modal.alert("提示", content, [
      {
        text: "重新选择油枪号",
        onPress: () => {
          setCurrentGunId(null);
          setOrders([]);
        },
      },
    ]);
  };

  // 统一的"查不到单"兜底：重试（回选油枪号） / 手动输入
  const promptRetryOrManual = (content: string) => {
    if (gatherTimer.current) clearTimeout(gatherTimer.current);
    Modal.alert("提示", content, [
      {text: "手动输入", onPress: () => goManual()},
      {
        text: "重试",
        onPress: () => {
          setCurrentGunId(null);
          setOrders([]);
        },
      },
    ]);
  };

  // 单次查单 + 按需轮询（照小程序 queryGather :411-461）
  const queryGather = async (id: any) => {
    try {
      const res: any = await gatherList({room_id: userInfo.room_id, cate_id: id});
      const body = pickGatherBody(res);
      const code = body && body.code;

      // ① 采集器掉线 / 后台强制手动 → 站级故障，自动切手动
      if (code === "MANUAL_MODE") {
        Toast.hide();
        return goManual();
      }

      // ② 暂无订单 → 每 retryAfterMs 轮询，最多 15s；到点仍无单 → 只让重选枪号
      if (code === "NO_ORDER_YET") {
        const retryAfter = Number(body.retryAfterMs) || 1500;
        const elapsed = Date.now() - gatherStartAt.current;
        if (elapsed + retryAfter >= NO_ORDER_TIMEOUT_MS) {
          Toast.hide();
          return promptRetry("暂无订单，请确定枪号");
        }
        gatherTimer.current = setTimeout(() => queryGather(id), retryAfter);
        return;
      }

      // ③ 油品不匹配（也属枪号/油品选错）→ 只让重选枪号
      if (code === "FUEL_MISMATCH") {
        Toast.hide();
        setOrders([]);
        return promptRetry(body.message || "油品不匹配，请确认油枪号");
      }

      // ④ 命中（ok:true）或旧结构：有 candidates 就显示
      Toast.hide();
      const list = (body && body.candidates) || (res && res.data && res.data.candidates) || [];
      if (list.length) {
        setOrders(list);
        setSelectedId(0);
      } else {
        setOrders([]);
        promptRetryOrManual("未获取到订单，请确认油枪号");
      }
    } catch (e) {
      // 网络异常：不自动切手动（网络可能恢复），弹窗让用户选
      console.log(e);
      Toast.hide();
      promptRetryOrManual("网络异常，获取订单失败，请重试");
    }
  };

  // 选油枪后触发查单（照小程序 getOrderMoney）
  const getOrderMoney = async (id: any) => {
    if (gatherTimer.current) clearTimeout(gatherTimer.current); // 换枪时取消上一把枪的轮询
    gatherStartAt.current = Date.now();
    Toast.loading("查询中", 0);
    await queryGather(id);
  };

  const handleOilClick = (index: number) => {
    setCurrentOilIdx(index);
    setCurrentGunId(null);
    setOrders([]);
  };

  const handleGunClick = (gun: DisplayGun) => {
    // "其它" = 列表里没列出来的枪：弹窗让用户手输枪号再查单
    if (gun.id === "other") {
      Modal.prompt(
        "请输入油枪号",
        "",
        [
          {text: "取消"},
          {
            text: "确定",
            onPress: (v: any) => {
              const val = (v || "").trim();
              if (!val) return Toast.info("请输入油枪号");
              setCurrentGunId("other");
              getOrderMoney(val);
            },
          },
        ],
        "default",
        ""
      );
      return;
    }
    setCurrentGunId(gun.id);
    getOrderMoney(gun.id);
  };

  // 确定订单（照小程序 confirmOrder 的 new 分支）
  const confirmOrder = async () => {
    const order_item = orders.find((_item, index) => index === Number(selectedId));
    if (!order_item) return Toast.info("请选择订单");

    const children = (oilList[currentOilIdx] && oilList[currentOilIdx].children) || [];
    const now_item = children.find((c: any) => c.id === currentGunId) || children[0];
    if (!now_item) return Toast.info("油品信息异常，请手动选择");

    Toast.loading("请稍候...", 0);
    // 与 store_oil.setNowOilInfo 一致：备好储值/平台卡数据，再进付款页
    await disPatch(payStore.setManageCardList(now_item.goods_id, now_item.goods.type.id));
    await disPatch(payStore.setUserBuyCardList(now_item.goods_id, now_item.goods.type.id));
    await disPatch(payStore.setRechargeInfo({cate_id: now_item.id, room_id: userInfo.room_id, money: 0}));
    // 查单模式：暂存选中候选（下单时透传 gather_order）+ 当前油枪
    await disPatch(setGatherOrder(order_item));
    await disPatch(setOilType(now_item));
    Toast.hide();
    // price_lock=1 通知付款页锁定金额（金额为后端查到的真实订单金额，不允许改价）
    navigate(`/userPay?price_lock=1&receipt=${is_show_receipt}`);
  };

  const displayGuns = getDisplayGuns();

  return (
    <div className="linkage-con">
      {/* 站点信息卡 */}
      <div className="station-card">
        <div className="station-img">
          <img alt="门店logo" src={userInfo.room_image} />
        </div>
        <div className="station-main">
          <h6>{userInfo.room_name}</h6>
          <span>{userInfo.room_area}</span>
        </div>
      </div>

      {/* 选择油号 */}
      <div className="selection-box">
        <div className="section-title">选择油号</div>
        <div className="btn-group">
          {oilList.map((item, index) => (
            <div
              key={index}
              className={`btn-item ${currentOilIdx === index ? "active" : ""}`}
              onClick={() => handleOilClick(index)}
            >
              {item.name}
            </div>
          ))}
        </div>
      </div>

      {/* 选择油枪 */}
      <div className="selection-box">
        <div className="section-title">
          选择油枪
          <span className="hint">请向加油员确认油枪号</span>
        </div>
        <div className="btn-group">
          {displayGuns.map((gun) => (
            <div
              key={String(gun.id)}
              className={`btn-item ${currentGunId === gun.id ? "active" : ""}`}
              onClick={() => handleGunClick(gun)}
            >
              {gun.name.replace(/油枪/g, "")}
            </div>
          ))}
        </div>
      </div>

      {/* 订单候选 */}
      {orders.length > 0 && (
        <div className="order-container">
          <div className="order-title">选择油品订单</div>
          <div className="order-list">
            {orders.map((item, index) => {
              const isSelected = index === selectedId;
              return (
                <div
                  key={index}
                  className={`order-card ${isSelected ? "active" : ""}`}
                  onClick={() => setSelectedId(index)}
                >
                  <div className="radio-wrapper">
                    <div className={`radio-core ${isSelected ? "checked" : ""}`} />
                  </div>
                  <div className="order-info">
                    <div className="info-top">
                      <span className="fuel-tag">{item.fuelCode}</span>
                      <span className="pump-text">{item.orderNo}</span>
                    </div>
                    <div className="info-bottom">
                      <span className="time-text">{item.collectedAt}</span>
                      <span className="price-text">￥{item.amount}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 底部按钮 */}
      <div className="order-action-box">
        <div className="confirm-btn" onClick={() => confirmOrder()}>
          确认加油 / 选择订单
        </div>
        {/* <div className="manual-btn" onClick={() => goManual()}>
          手输
        </div> */}
      </div>
      <Copyright />
    </div>
  );
};

export default OilEngineLinkage;
